import { Trans, t } from '@lingui/macro';
import { useLingui } from "@lingui/react";
import { EventEmitter } from "events";
import { Plus, Pin, Pencil } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { Room } from "../../../models/Room";
import { RoomItem } from "../../../models/RoomItem";
import { My } from "../../../services/My";
import { Util } from "../../../services/Utils/Util";
import { getDisplayName } from "../../../services/Utils/userDisplayName";
import MemoOpenModal from "./MemoOpenModal";
import { TextAssistantModalCtrl } from "./TextAssistantModal";
import { SectionCard, SectionActionButton, SectionListItem } from "./SectionCard";
import { UserAvatar } from "../../components/UserAvatar";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { useToast } from "../../../hooks/use-toast";

export const MemoModalEmitter = new EventEmitter();
export var LatestItems: RoomItem[] = [];

export const PanelMemos: FC<{ room: Room, items: RoomItem[] }> = ({ room, items }) => {
  useLingui();
  const { toast } = useToast();
  const [selectedItem, setSelectedItem] = useState<RoomItem>();
  const [addText, setAddText] = useState<string | null>(null);

  useEffect(() => {
    const handleView = (itemId: string) => {
      openSelectedItem(LatestItems.find(i => i.id === itemId))
    };
    const handleCreate = (text: string) => {
      createMemo(text?.replaceAll('\n', '<br/>'));
    };
    const handleAdd = (itemId: string, text: string) => {
      const item = LatestItems.find(i => i.id === itemId)
      if (item) {
        setAddText(text);
        setSelectedItem(item);
      } else {
        createMemo(text?.replaceAll('\n', '<br/>'));
        setTimeout(() => {
          toast({ title: t`Error`, description: t`Could not open the memo. Created a new instead.`, duration: 2000 });
        }, 1000);
      }
    };
    const handleTextAssistant = (text: string) => {
      TextAssistantModalCtrl.open(room, Util.getTextAsPlain(text), LatestItems);
    };

    MemoModalEmitter.on('view', handleView);
    MemoModalEmitter.on('create', handleCreate);
    MemoModalEmitter.on('add', handleAdd);
    MemoModalEmitter.on('text-assistant', handleTextAssistant);

    return () => {
      MemoModalEmitter.removeListener('view', handleView);
      MemoModalEmitter.removeListener('create', handleCreate);
      MemoModalEmitter.removeListener('add', handleAdd);
      MemoModalEmitter.removeListener('text-assistant', handleTextAssistant);
    }
  }, [room.id])

  useEffect(() => {
    LatestItems = items;
    if (selectedItem) {
      const item = items.find(i => i.id === selectedItem.id);
      if (item) {
        setSelectedItem(item);
      }
    }
  }, [items])

  return <>
    <SectionCard
      title={t`Memos`}
      collapsible={false}
      actions={
        !room.isReadOnly ? (
          <SectionActionButton
            onClick={() => createMemo()}
            icon={<Plus className="w-4 h-4" />}
            label={t`Added memos`}
          />
        ) : undefined
      }
    >
      {items.length > 0 ? (
        <div className="flex flex-col">
          {items.map((i) => (
            <SectionListItem
              key={i.id}
              onClick={() => openSelectedItem(i)}
              className="group"
            >
              {/* 아이콘 영역 */}
              <div className="flex items-center gap-1 text-muted-foreground">
                {i.pinned && <Pin className="w-3.5 h-3.5 text-primary" />}
                {i.lockedByUserId && <Pencil className="w-3.5 h-3.5 text-sky-500" />}
              </div>

              {/* 메모 제목 */}
              <span className="flex-1 text-sm text-[color:rgb(var(--text-body))] truncate group-hover:text-primary transition-colors">
                {i.title || t`Untitled memo`}
              </span>

              {/* 작성자 */}
              <div className="flex items-center gap-1.5 shrink-0">
                <UserAvatar src={i.user.photoUrl} name={getDisplayName(i.user)} className="w-5 h-5" />
                <span className="text-xs text-muted-foreground max-w-[60px] truncate">
                  {getDisplayName(i.user)}
                </span>
              </div>
            </SectionListItem>
          ))}
        </div>
      ) : (
        <p className="text-body font-medium text-muted-foreground py-3 text-center">
          <Trans>No memos yet</Trans>
        </p>
      )}
    </SectionCard>

    {selectedItem && <MemoOpenModal item={selectedItem} addText={addText} onClose={() => setSelectedItem(undefined)} />}
  </>

  function openSelectedItem(i: RoomItem | undefined) {
    if (i) {
      setAddText(null);
      setSelectedItem(i);
    }
  }

  function createMemo(text: string = '') {
    const item = new RoomItem(room.id, My.user);
    item.type = 'memo';
    item.text = text;
    item.editableBy = 'everyone';
    item.id = ''; // Tells MemoOpenModal that the item is not saved yet.
    setAddText(null);
    setSelectedItem(item);
  }
}
